import { Container, Graphics, Sprite } from 'pixi.js'
import type { Decoration, LightsOsDocument, Point, Strand } from '../types/document'
import { isDecoration, isStrand } from '../types/document'
import type { RoofProposal } from '../types/proposal'
import { PRODUCTS } from '../lib/products'
import { hexToNumber } from '../lib/palettes'
import { assetTexture } from './assets'
import { defaultStyleFor, garlandSegment, styleById } from '../lib/decorLibrary'
import {
  measuredFeet,
  pathFeet,
  pathLength,
  sampleStrand,
  scallop,
  segmentLength,
  toPx,
  type PxPoint,
} from '../lib/geometry'
import {
  getBounceTexture,
  getGlintTexture,
  getSpikeTexture,
  getCoreTexture,
  getGlowTexture,
  getSpillTexture,
  getStreakTexture,
  getVeilTexture,
} from './textures'

/**
 * The renderer is a pure function of the design document.
 * Nothing in here reads UI state, and nothing outside here knows how a bulb
 * is drawn. That separation is what lets the same code render a live preview,
 * a full resolution export, and later a server side proposal image.
 */

export interface SceneRefs {
  world: Container
  photoLayer: Container
  spillLayer: Container
  wireLayer: Graphics
  glowLayer: Container
  coreLayer: Container
  /** Company logo. Above the lights, below the editing overlay, so it exports. */
  brandLayer: Container
  overlay: Graphics
}

export interface StrandMetrics {
  id: string
  name: string
  bulbs: number
  pixels: number
  feet: number | null
}

export interface RenderStats {
  totalBulbs: number
  totalFeet: number | null
  perStrand: StrandMetrics[]
}


/**
 * HOW A LIT LAMP READS. Every number here is a look, not a mechanism.
 *
 * A lit bulb in a photograph is four things stacked: a blown out core that has
 * clipped the sensor, a tight bright halo around it, a much wider and much
 * fainter bloom, and a general veil over the whole display. Getting the ratio
 * between them right is what separates countable lamps from a rope of light,
 * and a glowing ball from something that looks switched on.
 *
 * Collected here so the look can be swept against a NIGHT background - which
 * is the only place it can honestly be judged - without editing the renderer
 * between attempts. ?lptest=1 lets the harness override them.
 */
export interface BulbLook {
  /** Wide, very faint wash over the whole display. */
  veilSize: number
  veilAlpha: number
  /** The broad bloom. Bigger than strict accuracy asks for - see the note. */
  outerSize: number
  outerAlpha: number
  /** The tight halo that makes it read as a lamp rather than a dot. */
  innerSize: number
  innerAlpha: number
  /** The glass, tinted toward white because a filament clips. */
  coreSize: number
  coreWhiteness: number
  coreAlpha: number
  /** The clipped center. */
  hotSize: number
  hotAlpha: number
}

/**
 * MEASURED OFF A REAL PHOTOGRAPH OF A LIT C9 ROOFLINE, NOT CHOSEN.
 *
 * A radial profile across twelve isolated bulbs on that reference, expressed
 * as a fraction of the bulb spacing so the scale of the photograph cannot
 * confuse it:
 *
 *     blown core   0.24 x spacing
 *     half max     0.43
 *     quarter max  0.54
 *     faint tail   0.70   - indistinguishable from the fascia beyond this
 *
 * What shipped before measured 0.24 / 0.47 / 0.82 / 3.41. The core was
 * already right and the BLOOM was five times too wide: it reached 1.7 times
 * the bulb spacing, so every run overlapped itself and read as a rope of light
 * rather than as countable lamps. That was the whole realism gap, and it was
 * not where I had been looking - three earlier variants tuned the core while
 * the error sat entirely in the tail.
 *
 * A note in the previous version justified the wide bloom on the grounds that
 * its reference was one gable filling a 3024px frame while a mockup is a whole
 * house in 1536. That reasoning does not apply here: the new reference IS a
 * whole house at 1536, which is exactly the scale we render at, so it
 * supersedes it.
 *
 * These values measure 0.24 / 0.35 / 0.59 / 1.06 - much closer, and the
 * remaining tail excess is deliberate. A mockup is looked at on a phone in a
 * driveway and a little more reach than strict accuracy survives that.
 */
export const BULB: BulbLook = {
  veilSize: 12,
  veilAlpha: 0.1,
  outerSize: 3.4,
  outerAlpha: 0.55,
  innerSize: 2.2,
  innerAlpha: 1.15,
  coreSize: 1.45,
  coreWhiteness: 0.42,
  coreAlpha: 1.05,
  hotSize: 1.15,
  hotAlpha: 1.45,
}

/**
 * A softer fallback, kept for a house photographed close enough that the
 * shipping values read austere.
 *
 * Not wired to anything. It exists so the decision is recorded rather than
 * rediscovered: it measures a tail of 1.29 against the reference 0.70, which
 * is looser than what ships and still nothing like the 3.41 that came before.
 */
export const BULB_SOFT: BulbLook = {
  veilSize: 16,
  veilAlpha: 0.12,
  outerSize: 4.6,
  outerAlpha: 0.48,
  innerSize: 2.4,
  innerAlpha: 1.1,
  coreSize: 1.45,
  coreWhiteness: 0.42,
  coreAlpha: 1.05,
  hotSize: 1.15,
  hotAlpha: 1.45,
}

/** Live override for the tuning harness. Never used in normal running. */
export function setBulbLook(patch: Partial<BulbLook>): void {
  Object.assign(BULB, patch)
}

export function createSceneRefs(): SceneRefs {
  const world = new Container()
  world.label = 'world'

  const photoLayer = new Container()
  const spillLayer = new Container()
  const wireLayer = new Graphics()
  const glowLayer = new Container()
  const coreLayer = new Container()
  const brandLayer = new Container()
  const overlay = new Graphics()

  spillLayer.blendMode = 'add'
  glowLayer.blendMode = 'add'
  coreLayer.blendMode = 'add'

  world.addChild(
    photoLayer,
    spillLayer,
    wireLayer,
    glowLayer,
    coreLayer,
    brandLayer,
    overlay,
  )
  return {
    world,
    photoLayer,
    spillLayer,
    wireLayer,
    glowLayer,
    coreLayer,
    brandLayer,
    overlay,
  }
}

/** Place the company logo in a corner of the photo, at photo scale. */
export function layoutBranding(
  sprite: Sprite,
  doc: LightsOsDocument,
): void {
  const photo = doc.photo
  if (!photo) return
  const { width: W, height: H } = photo
  const b = doc.branding
  const pad = W * 0.028

  const w = W * b.scale
  const ratio = sprite.texture.height / sprite.texture.width || 0.4
  const h = w * ratio
  sprite.width = w
  sprite.height = h
  sprite.alpha = b.opacity

  const left = pad
  const right = W - w - pad
  const top = pad
  const bottom = H - h - pad

  switch (b.corner) {
    case 'tl':
      sprite.position.set(left, top)
      break
    case 'tr':
      sprite.position.set(right, top)
      break
    case 'bl':
      sprite.position.set(left, bottom)
      break
    default:
      sprite.position.set(right, bottom)
  }
}

/** Stable pseudo random per bulb, so bulbs vary but do not shimmer on redraw. */
function jitter(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

/**
 * The color a surface goes under a lamp of this color.
 *
 * The first version of this halved the blue channel flat, on the grounds that
 * the reference photograph shows warm white light landing on tile as
 * (116, 102, 56) - very little blue. That is true and it was still wrong, for
 * a reason that only shows up on the colors nobody had drawn yet: a BLUE bulb
 * throws blue on the wall, and halving its blue is the one thing you must not
 * do. It would have quietly ruined every blue and multicolor job.
 *
 * The real mechanism is not "less blue", it is that a surface is never as
 * saturated as the lamp lighting it - stucco and clay have a color of their
 * own and they mute whatever lands on them. Checked against the measurement:
 * the bulb is (255, 181, 90), the tile it lights is (116, 102, 56), which is
 * the bulb pulled about a third of the way toward its own gray. That single
 * rule reproduces the warm case and leaves a blue bulb blue.
 */
function surfaceUnder(c: number): number {
  const r = (c >> 16) & 255
  const g = (c >> 8) & 255
  const b = c & 255
  const gray = 0.299 * r + 0.587 * g + 0.114 * b
  /*
   * How far toward its own gray. Set from the same measurement: at a third the
   * wash came out neutral once it was summed onto an evening ambient that is
   * itself slightly blue, and warm light on the house is most of what says
   * Christmas rather than street lighting.
   */
  const mute = 0.18
  const m = (v: number) => Math.round(v + (gray - v) * mute)
  return (m(r) << 16) | (m(g) << 8) | m(b)
}

/** Blend two packed RGB colors. Used to take a lit core toward white. */
function mixTint(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255
  const ag = (a >> 8) & 255
  const ab = a & 255
  const br = (b >> 16) & 255
  const bg = (b >> 8) & 255
  const bb = b & 255
  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const bl = Math.round(ab + (bb - ab) * t)
  return (r << 16) | (g << 8) | bl
}

function expandPalette(palette: string[], repeat: number): number[] {
  const colors = palette.length ? palette : ['#FFB55A']
  const out: number[] = []
  for (const c of colors) {
    for (let i = 0; i < Math.max(1, repeat); i++) out.push(hexToNumber(c))
  }
  return out
}

interface SizeBasis {
  /** Pixels per inch in the photo, or null when the photo is not calibrated. */
  pxPerInch: number | null
  photoWidth: number
}

/**
 * HOW BIG A LIT BULB IS ON SCREEN.
 *
 * THIS WAS THE BUG BEHIND "the bulbs are not great", and it only appeared once
 * a photo had a scale - which is to say, on every real job and on none of the
 * test shots I had been judging by eye.
 *
 * Uncalibrated, the size came from a fraction of the photo width and the run
 * looked reasonable. Calibrated, it came from the true glass diameter, and a
 * C9 is 1.13 inches: on a house photographed at three pixels to the inch that
 * is a three pixel bulb, which at fit-to-window is under two pixels on screen.
 * Setting the scale - the thing the app nags you to do - made the lights
 * almost disappear. Two paths, two different answers, and the wrong one was
 * the one a real job took.
 *
 * There is one path now, and one number behind it, measured off the reference
 * photograph: on a straight eave the bulbs are 50px apart and the blown out
 * core of each is 17px, so a lit C9 covers just over a third of the gap to its
 * neighbor. Working back through the same 12 inch spacing, the lit lamp reads
 * about 3.6 times the width of its own glass. That is not artistic license, it
 * is what a filament orders of magnitude brighter than the scene does to a
 * phone sensor, and every product carries its own version of it as litScale.
 *
 * The uncalibrated case now goes through the SAME formula with an assumed
 * scale, so the two can no longer drift apart.
 */
function bulbSizePx(strand: Strand, basis: SizeBasis): number {
  const spec = PRODUCTS[strand.product]
  /*
   * With no scale, assume the run is spaced the way the fallback spacing
   * assumes - a foot every 0.021 of the photo width - so that a bulb keeps the
   * same relationship to its neighbors either way.
   */
  const pxPerInch = basis.pxPerInch ?? (basis.photoWidth * 0.021) / 12
  const px = spec.bulbInches * pxPerInch * spec.litScale
  /*
   * A floor, in fractions of the photo. Below about three pixels a lamp is a
   * speck and you cannot tell a lit tree from a dark one. It scales with the
   * photo rather than the screen, so the export and the fit-to-window view
   * agree. With the sizing above it rarely binds; it is here for the tiny
   * products on a small photo.
   */
  const floor = basis.photoWidth * 0.0015
  return Math.max(1.2, Math.max(px, floor) * strand.bulbScale)
}

/**
 * THE PICTURE AND THE PARTS LIST HAVE TO AGREE.
 *
 * The drawn spacing came from the product spec while the materials list counted
 * bulbs at the takeoff spacing, so choosing a wider spacing changed what the
 * quote charged for and left the mockup showing the old bulb count. A
 * contractor would have been reading a picture with one number of lamps on it
 * and handing over a price for another.
 *
 * The takeoff spacing is a ROOF setting, so it applies to roofline runs and to
 * nothing else - a tree wrap is whole finished strands with the lamp pitch
 * built in, and a swag hangs at the garland's own pitch.
 */
function spacingPx(strand: Strand, basis: SizeBasis, roofSpacingInches?: number): number {
  const spec = PRODUCTS[strand.product]
  const inches =
    strand.kind === 'roofline' && roofSpacingInches && roofSpacingInches > 0
      ? roofSpacingInches
      : spec.spacingInches
  const px = basis.pxPerInch
    ? inches * basis.pxPerInch
    : basis.photoWidth * 0.021 * (inches / 12)
  return Math.max(3, px * strand.spacingScale)
}

/** Clear a container without leaking textures (they are shared and reused). */
function reset(container: Container) {
  container.removeChildren().forEach((c) => c.destroy({ children: true, texture: false }))
}

export interface DraftPreview {
  points: Point[]
  product: Strand['product']
  palette: string[]
  patternRepeat: number
  bulbScale: number
  spacingScale: number
  brightness: number
  sag: number
  depthStart: number
  depthEnd: number
  kind?: Strand['kind']
  wrapWidth?: number
  wrapTurns?: number
  wrapCap?: boolean
  /** Unused since the cap became a band of the wrap. Kept: schema, not paint. */
  capFronds?: number
  capDroop?: number
  capPalette?: string[]
  capLength?: number
}

export function renderLights(
  refs: SceneRefs,
  doc: LightsOsDocument,
  /** The run being drawn right now, so bulbs appear as you click. */
  preview?: DraftPreview | null,
): RenderStats {
  reset(refs.spillLayer)
  reset(refs.glowLayer)
  reset(refs.coreLayer)
  /*
   * clear() wipes a Graphics' own drawing. It does NOT remove its children.
   *
   * wireLayer is a Graphics that also carries sprites - every decoration's
   * artwork and every tile of garland is added to it as a child - so for as
   * long as this said clear() and nothing else, each redraw stacked another
   * copy of every one of them on top of the last. Dozens deep, a wreath's
   * translucent inner edge accumulates until the hole fills in and it reads as
   * a solid green starburst, and the more you touch a slider the worse it gets.
   * That is the bug behind "the wreath doesn't resize, it just warps".
   */
  reset(refs.wireLayer)
  refs.wireLayer.clear()

  const photo = doc.photo
  if (!photo) return { totalBulbs: 0, totalFeet: null, perStrand: [] }

  const { width: W, height: H } = photo
  const { night, spill, glow } = doc.scene
  const bounce = doc.scene.bounce ?? 0.85

  // The night conversion itself lives in the night-graded copy of the photo,
  // cross-faded over the original by StageView. Nothing to paint here: a flat
  // veil over the whole frame is exactly the mistake this replaced.

  const ppf = doc.calibration.pixelsPerFoot
  const basis: SizeBasis = { pxPerInch: ppf ? ppf / 12 : null, photoWidth: W }

  const glowTex = getGlowTexture()
  const coreTex = getCoreTexture()
  const spillTex = getSpillTexture()
  const bounceTex = getBounceTexture()
  const veilTex = getVeilTexture()
  const streakTex = getStreakTexture()
  const spikeTex = getSpikeTexture()
  const glintTex = getGlintTexture()

/*
   * HOW BIG THE LIGHT AROUND A BULB IS.
   *
   * These were tuned looking at one bulb at a time, and one bulb at a time they
   * were fine. On a real run they are not: the halos are additive and the bulbs
   * are a foot apart, so a spill 26 bulb-widths across reaches two neighbors
   * in each direction and five of them sum into a solid white rope with the
   * individual lamps lost inside it. A C9 at night reads as a distinct point
   * with a modest halo, which is the whole reason anyone can count them in a
   * photograph.
   *
   * Halved, and the wide spill now falls off with how densely the run is
   * packed, so a tight mini-light strand does not blow out where a spaced C9
   * run looks right.
   */
  const paintBulb = (
    bx: number,
    by: number,
    size: number,
    tintC: number,
    bright: number,
    angle = 0,
    /** Bulb pitch in pixels. Closer together means each one spills less. */
    pitch = 0,
  ) => {
    // 1 at C9 spacing and below, falling as the bulbs crowd together.
    const crowd = pitch > 0 ? Math.max(0.35, Math.min(1, pitch / (size * 9))) : 1
    const dayness = 1 - night
    if (dayness > 0.02) {
      refs.wireLayer.circle(bx, by, size * 0.95).fill({ color: 0x2a1f12, alpha: 0.55 * dayness })
    }
    if (spill > 0.01) {
      const sp = new Sprite(spillTex)
      sp.anchor.set(0.5)
      sp.position.set(bx, by)
      sp.width = sp.height = size * 9 * spill
      sp.tint = tintC
      sp.alpha = 0.17 * spill * bright * crowd
      refs.spillLayer.addChild(sp)
    }
    if (bounce > 0.01 && night > 0.04) {
      const bo = new Sprite(bounceTex)
      bo.anchor.set(0.5, 0)
      bo.position.set(bx, by + size * 0.35)
      bo.width = size * 18 * bounce
      bo.height = size * 13 * bounce
      bo.tint = tintC
      bo.alpha = 0.22 * bounce * bright * night * crowd
      refs.spillLayer.addChild(bo)
    }
    const gO = new Sprite(glowTex)
    gO.anchor.set(0.5)
    gO.position.set(bx, by)
    gO.width = gO.height = size * 4.4 * glow
    gO.tint = tintC
    gO.alpha = 0.15 * bright * crowd
    refs.glowLayer.addChild(gO)

    const gI = new Sprite(glowTex)
    gI.anchor.set(0.5)
    gI.position.set(bx, by)
    gI.width = gI.height = size * 2.3 * glow
    gI.tint = tintC
    gI.alpha = 0.62 * bright
    refs.glowLayer.addChild(gI)

    const c = new Sprite(coreTex)
    c.anchor.set(0.5)
    c.position.set(bx, by)
    c.width = c.height = size * 1.5
    c.rotation = angle
    // See the note on the core in the main loop: a lit bulb clips to white.
    c.tint = mixTint(tintC, 0xffffff, 0.34)
    c.alpha = Math.min(1, 1.05 * bright)
    refs.coreLayer.addChild(c)

    const hot = new Sprite(coreTex)
    hot.anchor.set(0.5)
    hot.position.set(bx, by)
    hot.width = hot.height = size * 1.05
    hot.rotation = angle
    hot.tint = 0xfbfff6
    hot.alpha = Math.min(1, 0.85 * bright)
    refs.coreLayer.addChild(hot)
  }

  /*
   * Greenery along a path, for garland.
   *
   * Garland was drawing as a bare string of bulbs, which is not what anyone
   * hangs over a garage door: the lights are wound through a rope of
   * greenery, and without the rope it reads as a wire in mid air. Same
   * technique as the wreath, laid along the run instead of around a circle:
   * a dark core for mass, then needles fanning out on both sides to break
   * the silhouette so it never looks like a drawn line.
   */
  const paintGreenery = (pts: PxPoint[], thickness: number, seed0: number) => {
    if (pts.length < 2) return
    const GREENS = [0x0a1f0e, 0x102a14, 0x16371a, 0x1d4520, 0x255328, 0x2f6432]
    // Mass first.
    refs.wireLayer.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) refs.wireLayer.lineTo(pts[i].x, pts[i].y)
    refs.wireLayer.stroke({
      width: thickness,
      color: GREENS[1],
      alpha: 1,
      cap: 'round',
      join: 'round',
    })

    // Then needles, evenly spaced along the run with jitter so the edge is
    // ragged rather than parallel.
    let total = 0
    for (let i = 1; i < pts.length; i++) total += segmentLength(pts[i - 1], pts[i])
    const n = Math.round(Math.min(900, Math.max(80, total / (thickness * 0.16))))
    for (let k = 0; k < n; k++) {
      const u = k / n
      const at = u * (pts.length - 1)
      const i0 = Math.min(pts.length - 2, Math.floor(at))
      const f = at - i0
      const a0 = pts[i0]
      const a1 = pts[i0 + 1]
      const bx = a0.x + (a1.x - a0.x) * f
      const by = a0.y + (a1.y - a0.y) * f
      const tangent = Math.atan2(a1.y - a0.y, a1.x - a0.x)
      const sd = seed0 + k * 3.77
      const side = k % 2 === 0 ? 1 : -1
      const dir = tangent + side * (Math.PI / 2) + (jitter(sd) - 0.5) * 1.6
      const L = thickness * (0.32 + jitter(sd + 91) * 0.62)
      const idx = Math.min(5, (k % 2 === 0 ? 0 : 3) + Math.floor(jitter(sd + 17) * 2.4))
      // The underside of a garland hangs in its own shadow.
      const shade = side > 0 ? 0.72 : 1
      const ox = (jitter(sd + 53) - 0.5) * thickness * 0.5
      const oy = (jitter(sd + 71) - 0.5) * thickness * 0.5
      refs.wireLayer
        .moveTo(bx + ox, by + oy)
        .lineTo(bx + ox + Math.cos(dir) * L, by + oy + Math.sin(dir) * L)
        .stroke({
          width: Math.max(0.5, thickness * 0.1),
          color: GREENS[idx],
          alpha: 0.92 * shade,
          cap: 'round',
        })
    }
  }

  const perStrand: StrandMetrics[] = []
  let totalBulbs = 0
  let totalPixels = 0

  const drawable: Strand[] = doc.layers.filter((l): l is Strand => isStrand(l) && l.visible)
  if (preview && preview.points.length >= 2) {
    // Bulbs appear while you are still clicking. Watching a bare line grow and
    // wondering whether the tool is working is the worst possible first minute.
    drawable.push({
      id: '__draft__',
      type: 'strand',
      kind: preview.kind ?? 'roofline',
      name: 'Drawing',
      visible: true,
      points: preview.points,
      product: preview.product,
      palette: preview.palette,
      patternRepeat: preview.patternRepeat,
      bulbScale: preview.bulbScale,
      spacingScale: preview.spacingScale,
      brightness: preview.brightness,
      sag: preview.sag,
      depthStart: preview.depthStart,
      depthEnd: preview.depthEnd,
      wrapWidth: preview.wrapWidth ?? 0.028,
      wrapTurns: preview.wrapTurns ?? 14,
      wrapCap: preview.wrapCap ?? false,
      capFronds: preview.capFronds ?? 9,
      capDroop: preview.capDroop ?? 0.55,
      capPalette: preview.capPalette ?? ['#FF3320'],
      capLength: preview.capLength ?? 0.05,
    })
  }

  for (const strand of drawable) {
    const px: PxPoint[] = strand.points.map((p) => toPx(p, W, H))
    if (px.length < 2) continue
    const isDraft = strand.id === '__draft__'

    const spec = PRODUCTS[strand.product]
    const baseSize = bulbSizePx(strand, basis)
    const baseSpacing = spacingPx(strand, basis, doc.takeoff?.roofSpacingInches)
    let samples = sampleStrand(px, baseSpacing, strand.depthStart, strand.depthEnd, strand.sag)
    const colors = expandPalette(strand.palette, strand.patternRepeat)

    /*
     * A BUSH IS AN AREA, NOT A LINE.
     *
     * Squiggling over a shrub and getting a single wire of bulbs threading
     * through it is not what anybody meant, and it is not how the lights go on
     * either: a bush is netted or thrown over, so the bulbs sit scattered
     * across its face. So a freeform run spreads its bulbs into a band around
     * the stroke, and wrapWidth - the same "How big an area" slider - sets how
     * wide that band is. Squiggle roughly, then size the area.
     *
     * The offsets come from the same seeded jitter as everything else, so the
     * bulbs stay put across redraws instead of crawling around the bush.
     */
    let spine: typeof samples | null = null
    if (strand.kind === 'freeform' && samples.length) {
      const spread = Math.max(baseSpacing, W * strand.wrapWidth * 1.6)
      /*
       * A LIT BUSH IS A MOUND OF LIGHT, NOT A CONSTELLATION.
       *
       * Scattered lamps alone were disappearing into the shrub: each one is a
       * few pixels, the leaves behind them are dark and busy, and there is no
       * flat surface to catch a bounce the way a fascia does. Stand in front of
       * a netted bush at dusk and what you actually see first is the whole
       * shape glowing, with the individual lamps resolving inside it.
       *
       * So the glow goes down first, as a few wide soft blobs following the
       * stroke, and the bulbs land on top of it. That is what makes the shrub
       * read as switched on from across the street.
       */
      const warm = colors[0] ?? 0xffb55a
      const mound = 0.13 + 0.34 * night
      const step = Math.max(1, Math.round(samples.length / 14))
      for (let i = 0; i < samples.length; i += step) {
        const s0 = samples[i]
        const bl = new Sprite(spillTex)
        bl.anchor.set(0.5)
        bl.position.set(s0.x, s0.y)
        bl.width = spread * 3.4
        bl.height = spread * 2.6
        bl.tint = warm
        bl.alpha = mound * strand.brightness * 0.5
        refs.spillLayer.addChild(bl)
      }
      /*
       * "I see a lot of strings but I see less bulbs."
       *
       * The wire below was being run through the SCATTERED points - so once
       * the bulbs spread out over the bush, the wire zigzagged from one random
       * lamp to the next and the shrub filled up with string. On a real bush
       * the net is buried in the foliage and you barely see it at all.
       *
       * So the spine is kept here and the wire follows that instead: one quiet
       * line through the middle, with the lamps scattered over it.
       */
      spine = samples
      const per = 4
      const filled: typeof samples = []
      samples.forEach((s0, i) => {
        for (let k = 0; k < per; k++) {
          const seed = i * 7 + k * 137 + 3
          const a = jitter(seed) * Math.PI * 2
          // sqrt keeps the scatter even across the disc instead of clumping in
          // the middle, which is what makes it read as a filled bush.
          const rr = Math.sqrt(jitter(seed + 0.5)) * spread
          filled.push({
            ...s0,
            x: s0.x + Math.cos(a) * rr,
            y: s0.y + Math.sin(a) * rr * 0.8,
            scale: s0.scale * (0.85 + jitter(seed + 0.25) * 0.3),
          })
        }
      })
      samples = filled
    }

    /*
     * A trunk wrap is a spiral, not a line up the middle.
     *
     * The strand goes around the trunk, so roughly half the bulbs are on the
     * far side: visible, but dimmer and smaller, partly hidden by the trunk
     * itself. That front-to-back falloff is the entire reason a wrap reads as
     * wrapped. A straight line of bulbs up a palm fools nobody, and it is what
     * every competitor ships.
     */
    /*
     * One bulb, with everything a bulb has: the dark socket that keeps it
     * visible in daylight, the spill, the downward bounce, the two glow layers
     * and the hot filament core. Factored out here because a cluster paints
     * two rails of them and duplicating the body a third time is how renderers
     * start drifting out of sync with each other.
     */
    /*
     * A hanging cluster is a strand FOLDED IN HALF and hung from a branch.
     *
     * That is the detail that makes it read right and it is the one every
     * mockup tool misses: it is not a thin line of lights, it is two rows,
     * because the installer doubles the strand back on itself so the cluster
     * is twice as dense. The two rails run down side by side and turn through
     * a U at the bottom where the fold is.
     *
     * The bulbs on the two rails are offset by half a spacing. On the real
     * thing the two halves never line up, and staggering them is what stops it
     * reading as a ladder.
     */
    if (strand.kind === 'cluster') {
      /*
       * The two halves of a folded strand hang against each other, not spread
       * apart. It read as two separate strings at half a trunk width; the
       * point of doubling it is that it looks like one thick run.
       */
      const railGap = Math.max(1.5, W * strand.wrapWidth * 0.16)
      const spacing = baseSpacing
      const acc: PxPoint[] = px
      const samples = sampleStrand(acc, spacing, strand.depthStart, strand.depthEnd, 0)
      if (samples.length < 2) continue

      // A hung strand is never plumb. A gentle lean, seeded off the run so it
      // is stable across redraws but different from its neighbor.
      const leanSeed = jitter(px[0].x * 0.37 + px[0].y * 0.71)
      const lean = (leanSeed - 0.5) * railGap * 1.6

      const rails: { x: number; y: number; scale: number; angle: number }[][] = [[], []]
      samples.forEach((s2, i) => {
        const t = i / Math.max(1, samples.length - 1)
        const nx = Math.cos(s2.angle + Math.PI / 2)
        const ny = Math.sin(s2.angle + Math.PI / 2)
        // Rails splay very slightly toward the bottom, the way a doubled
        // strand hangs open under its own weight.
        const half = (railGap / 2) * (0.88 + 0.18 * t)
        const drift = lean * t * t
        for (const side of [0, 1]) {
          const sgn = side === 0 ? -1 : 1
          rails[side].push({
            x: s2.x + nx * half * sgn + drift,
            y: s2.y + ny * half * sgn,
            scale: s2.scale,
            angle: s2.angle,
          })
        }
      })

      // Wire down both rails and round the fold at the bottom.
      const wireW = Math.max(0.8, baseSize * 0.16)
      for (const rail of rails) {
        refs.wireLayer.moveTo(rail[0].x, rail[0].y)
        for (let i = 1; i < rail.length; i++) refs.wireLayer.lineTo(rail[i].x, rail[i].y)
        refs.wireLayer.stroke({
          width: wireW,
          color: hexToNumber(spec.wire),
          alpha: 0.55,
          cap: 'round',
        })
      }
      const aEnd = rails[0][rails[0].length - 1]
      const bEnd = rails[1][rails[1].length - 1]
      const foldDrop = railGap * 1.1
      refs.wireLayer
        .moveTo(aEnd.x, aEnd.y)
        .bezierCurveTo(
          aEnd.x,
          aEnd.y + foldDrop,
          bEnd.x,
          bEnd.y + foldDrop,
          bEnd.x,
          bEnd.y,
        )
        .stroke({ width: wireW, color: hexToNumber(spec.wire), alpha: 0.55, cap: 'round' })

      let count = 0
      rails.forEach((rail, side) => {
        rail.forEach((pt, i) => {
          // Half a spacing of offset between the two halves of the fold.
          if (side === 1 && i === 0) return
          const idx = side === 1 ? i * 2 + 1 : i * 2
          const tint = colors[idx % colors.length]
          const v = jitter(idx + 1)
          const bright = strand.brightness * spec.intensity * (0.9 + v * 0.18)
          const size = baseSize * pt.scale
          const stagger = side === 1 ? 0.5 : 0
          const prev = rail[Math.max(0, i - 1)]
          const sx = pt.x + (prev.x - pt.x) * stagger
          const sy = pt.y + (prev.y - pt.y) * stagger
          paintBulb(sx, sy, size, tint, bright, pt.angle)
          count++
        })
      })

      // Two bulbs riding the fold itself, so the bottom is not a bare wire.
      const midX = (aEnd.x + bEnd.x) / 2
      const midY = (aEnd.y + bEnd.y) / 2 + foldDrop * 0.72
      paintBulb(midX, midY, baseSize * aEnd.scale, colors[count % colors.length], strand.brightness * spec.intensity, 0)
      count++

      if (!isDraft) {
        const pixels = pathLength(px)
        totalPixels += pixels
        totalBulbs += count
        perStrand.push({
          id: strand.id,
          name: strand.name,
          bulbs: count,
          pixels,
          feet: ppf ? measuredFeet(px, ppf, strand, doc.takeoff) : null,
        })
      }
      continue
    }

    if (strand.kind === 'wrap') {
      const radius = (W * strand.wrapWidth) / 2
      const turns = Math.max(1, strand.wrapTurns)
      const pathLen = pathLength(px)
      // Length of the actual wire once it has been wound round the trunk.
      const perTurn = Math.hypot(2 * Math.PI * radius, pathLen / turns)
      const wireLen = perTurn * turns
      const count = Math.max(4, Math.round(wireLen / baseSpacing))

      const acc: number[] = [0]
      for (let i = 1; i < px.length; i++) acc.push(acc[i - 1] + segmentLength(px[i - 1], px[i]))
      const total = acc[acc.length - 1] || 1

      const at = (d: number) => {
        let lo = 0
        while (lo < acc.length - 2 && acc[lo + 1] < d) lo++
        const a = px[lo]
        const b = px[Math.min(lo + 1, px.length - 1)]
        const seg = (acc[lo + 1] ?? total) - acc[lo] || 1
        const u = Math.min(1, Math.max(0, (d - acc[lo]) / seg))
        return {
          x: a.x + (b.x - a.x) * u,
          y: a.y + (b.y - a.y) * u,
          nx: -(b.y - a.y) / (Math.hypot(b.x - a.x, b.y - a.y) || 1),
          ny: (b.x - a.x) / (Math.hypot(b.x - a.x, b.y - a.y) || 1),
        }
      }

      /*
       * THE CAP IS A BAND OF THE TRUNK, NOT A THING ON TOP OF IT.
       *
       * This used to draw a starburst of fronds above the trunk: spokes at
       * evenly spaced angles, invented, related to nothing in the photograph.
       * That is not what a cap is. Shown a real install - the trunk wrapped
       * warm white and the top foot or two of that same wrap in red - a cap is
       * a COLLAR of a different color, on the same spiral, at the same
       * spacing, at the same thickness.
       *
       * So it is the top slice of t, tinted differently. Thickness follows the
       * trunk because it IS the trunk, spacing follows for the same reason,
       * and the only two things left to decide are the color and how far down
       * it comes, which is exactly what was asked for.
       *
       * Which end is up is not ambiguous: the crown of a palm is the higher
       * end of its trunk, whichever end got tapped first.
       */
      const topIsFirst = px[0].y < px[px.length - 1].y
      const capBand = strand.wrapCap ? Math.min(0.6, Math.max(0.02, strand.capLength)) : 0
      const capColors = expandPalette(
        strand.capPalette?.length ? strand.capPalette : ['#E02B2B'],
        1,
      )

      // Back half first so the front of the trunk overlaps it.
      for (const pass of [0, 1]) {
        for (let i = 0; i <= count; i++) {
          const t = i / count
          const theta = t * turns * Math.PI * 2
          const front = Math.cos(theta)
          if (pass === 0 ? front >= 0 : front < 0) continue

          const c = at(t * total)
          const off = Math.sin(theta) * radius
          const bx = c.x + c.nx * off
          const by = c.y + c.ny * off

          const depth = (front + 1) / 2
          const scale = 0.62 + 0.38 * depth
          const size = baseSize * scale * (0.62 + 0.38 * (1 - t) + 0.38 * t)
          const inCap = capBand > 0 && (topIsFirst ? t <= capBand : t >= 1 - capBand)
          const tint = inCap ? capColors[i % capColors.length] : colors[i % colors.length]
          const v = jitter(i + 3)
          const bright = strand.brightness * spec.intensity * (0.9 + v * 0.18) * (0.34 + 0.66 * depth)

          if (spill > 0.01 && depth > 0.35) {
            const sp = new Sprite(spillTex)
            sp.anchor.set(0.5)
            sp.position.set(bx, by)
            sp.width = sp.height = size * 22 * spill
            sp.tint = tint
            sp.alpha = 0.42 * spill * bright
            refs.spillLayer.addChild(sp)
          }

          const dayness = 1 - night
          if (dayness > 0.02) {
            refs.wireLayer
              .circle(bx, by, size * 0.9)
              .fill({ color: 0x2a1f12, alpha: 0.5 * dayness * depth })
          }

          const g1 = new Sprite(glowTex)
          g1.anchor.set(0.5)
          g1.position.set(bx, by)
          g1.width = g1.height = size * 9 * glow
          g1.tint = tint
          g1.alpha = 0.5 * bright
          refs.glowLayer.addChild(g1)

          const c1 = new Sprite(coreTex)
          c1.anchor.set(0.5)
          c1.position.set(bx, by)
          c1.width = c1.height = size * 2
          c1.tint = tint
          c1.alpha = Math.min(1, bright)
          refs.coreLayer.addChild(c1)
        }
      }

      if (!isDraft) {
        const pixels = pathLength(px)
        totalPixels += pixels
        totalBulbs += count
        perStrand.push({
          id: strand.id,
          name: strand.name,
          bulbs: count,
          pixels,
          feet: ppf ? measuredFeet(px, ppf, strand, doc.takeoff) : null,
        })
      }
      continue
    }

    /*
     * Garland is a photographed product tiled along the run you drew.
     *
     * It used to be a placed arch you stretched to fit, which was the wrong
     * model and looked like a fat green tube: garland follows a LINE - a
     * garage header, a door frame, a railing - and stretching one arch can
     * never follow one. Drawing the line and laying real garland along it is
     * both the honest shape and the same two-click interaction as a roofline.
     *
     * The straight leg is tiled rather than the arch, for the obvious reason.
     */
    if (strand.kind === 'swag' && samples.length > 1) {
      const base = styleById(strand.styleId)
      const leg = garlandSegment(base?.label ?? 'Warm white', 'left')
      const tex = leg ? assetTexture(leg.url) : null

      if (tex && leg) {
        const thick = Math.max(4, W * strand.wrapWidth)
        // The asset is a tall straight run, so its long axis lies along the
        // path and its own width becomes the thickness.
        const tileLen = thick / leg.aspect

        /*
         * The three places garland actually goes.
         *
         * flush   - garage header, door or window frame. Straight on the line.
         * swags   - porch rail or fascia. Repeating scallops, fixed at each
         *           post and dipping between, NOT one long droop.
         * wrapped - spiraled round a post, column or rail.
         *
         * All three are the same drawn run and the same footage on the quote.
         * Only the path the product is laid along changes.
         */
        const shape = strand.garlandShape ?? 'flush'
        let raw = samples.map((q) => ({ x: q.x, y: q.y }))

        /*
         * ROUND THE CORNERS BEFORE TILING THEM.
         *
         * At a sharp corner two runs of tiles meet at ninety degrees, and each
         * one drives straight through the other - so the corner comes back as a
         * lump of overlapping greenery pointing two ways at once, which is
         * exactly what the garage jambs looked like.
         *
         * Real garland cannot turn a sharp corner either; it bends through an
         * arc of its own thickness. Rounding the path by roughly that radius
         * before laying anything on it means consecutive tiles never differ by
         * more than a few degrees, and the corner comes out as a curve because
         * that is what it physically is.
         */
        {
          const r = Math.max(6, W * strand.wrapWidth * 1.1)
          const rounded: PxPoint[] = [raw[0]]
          for (let i = 1; i < raw.length - 1; i++) {
            const p0 = raw[i - 1]
            const p1 = raw[i]
            const p2 = raw[i + 1]
            const l0 = segmentLength(p0, p1)
            const l1 = segmentLength(p1, p2)
            if (l0 < 1 || l1 < 1) continue
            const a0 = Math.atan2(p1.y - p0.y, p1.x - p0.x)
            const a1 = Math.atan2(p2.y - p1.y, p2.x - p1.x)
            let turn = Math.abs(a1 - a0)
            if (turn > Math.PI) turn = Math.PI * 2 - turn
            if (turn < 0.35) {
              rounded.push(p1)
              continue
            }
            // Cut the corner with a short arc between the two approach points.
            const cut = Math.min(r, l0 * 0.45, l1 * 0.45)
            const inX = p1.x - ((p1.x - p0.x) / l0) * cut
            const inY = p1.y - ((p1.y - p0.y) / l0) * cut
            const outX = p1.x + ((p2.x - p1.x) / l1) * cut
            const outY = p1.y + ((p2.y - p1.y) / l1) * cut
            const steps = 5
            for (let k = 0; k <= steps; k++) {
              const u = k / steps
              // Quadratic through the original corner: the arc garland makes.
              const mx = (1 - u) * (1 - u) * inX + 2 * (1 - u) * u * p1.x + u * u * outX
              const my = (1 - u) * (1 - u) * inY + 2 * (1 - u) * u * p1.y + u * u * outY
              rounded.push({ x: mx, y: my })
            }
          }
          rounded.push(raw[raw.length - 1])
          raw = rounded
        }
        const path =
          shape === 'swags'
            ? scallop(raw, Math.max(1, Math.round(strand.swagCount ?? 4)), Math.max(0.05, strand.sag))
            : raw
        let total = 0
        for (let i = 1; i < path.length; i++) total += segmentLength(path[i - 1], path[i])
        /*
         * OVERLAP HEAVILY. Garland is a rope, not a row of tufts.
         *
         * Laying the tiles end to end meant a garage jamb got two or three of
         * them, so every seam was a visible break and the run read as separate
         * bunches threaded on a wire. Real garland has no seams at all: it is
         * one continuous mass of greenery whose density never drops between one
         * handful and the next.
         *
         * Stepping at 45% of a tile length puts every point under two or three
         * overlapping pieces, so there is no position along the run where the
         * greenery thins out. It costs sprites and buys the one property that
         * makes it read as garland.
         */
        const count = Math.max(2, Math.round(total / (tileLen * 0.45)))

        /*
         * NOTHING HANGS PAST THE ENDS.
         *
         * Tiles were placed by fraction of the run and each is a couple of
         * tile-lengths long, so the first and last stuck out nearly half a tile
         * beyond where the garland actually stops. The leg artwork tapers, so
         * that overhang read as a spike poking off the end of every run - which
         * is exactly what it looked like on the arch corners.
         *
         * Centers are now laid out by ARC LENGTH between half a tile in from
         * each end, so the greenery starts and stops where the run does. Arc
         * length also fixes the spacing: the old version indexed by sample
         * number, which bunches tiles wherever the path happens to be sampled
         * more densely - at a rounded corner, for instance.
         */
        const acc: number[] = [0]
        for (let i = 1; i < path.length; i++) {
          acc.push(acc[i - 1] + segmentLength(path[i - 1], path[i]))
        }
        const half = Math.min(tileLen * 0.5, total * 0.42)
        const first = half
        const span = Math.max(1, total - half * 2)

        /** A point and a heading at a given distance along the run. */
        const walk = (dist: number) => {
          const d = Math.max(0, Math.min(total, dist))
          let k = 1
          while (k < acc.length - 1 && acc[k] < d) k++
          const segLen = Math.max(1e-6, acc[k] - acc[k - 1])
          const f = (d - acc[k - 1]) / segLen
          const p0 = path[k - 1]
          const p1 = path[k]
          return {
            x: p0.x + (p1.x - p0.x) * f,
            y: p0.y + (p1.y - p0.y) * f,
            ang: Math.atan2(p1.y - p0.y, p1.x - p0.x),
          }
        }

        /*
         * Two small tufts just inside the ends, to break the transverse cut the
         * last tile leaves. Barely turned: the first version swung them most of
         * a right angle to "round off" the end, and on a narrow garland that
         * reads as a spike rather than a bushy finish.
         */
        const caps: { at: number; turn: number }[] = [
          { at: 0.02, turn: 0.08 },
          { at: 0.98, turn: -0.08 },
        ]

        for (let i = 0; i < count + caps.length; i++) {
          const cap = i >= count ? caps[i - count] : null
          const t = cap ? cap.at : count === 1 ? 0.5 : i / (count - 1)
          const here = walk(cap ? total * cap.at : first + span * t)
          const bx = here.x
          const by = here.y
          const ang = here.ang

          /*
           * Wrapped garland corkscrews. Each piece rocks about the run and
           * rides out to one side and back, so the eye reads a spiral rather
           * than a straight rope - the same illusion a wrapped trunk uses, at
           * garland scale. wrapTurns is how tight the spiral is.
           */
          let px2 = bx
          let py2 = by
          let tilt = 0
          if (shape === 'wrapped') {
            const phase = t * Math.max(2, strand.wrapTurns) * Math.PI * 2
            const off = Math.sin(phase) * thick * 0.30
            px2 = bx - Math.sin(ang) * off * -1
            py2 = by + Math.cos(ang) * off
            tilt = Math.cos(phase) * 0.42
          }

          const sp = new Sprite(tex)
          sp.anchor.set(0.5)
          sp.position.set(px2, py2)
          // An end piece is turned across the run and shrunk, so the silhouette
          // rounds off instead of stopping on a straight cut.
          sp.rotation = ang + Math.PI / 2 + tilt + (cap ? cap.turn : 0)
          /*
           * Mirror every other tile.
           *
           * One photograph repeated down a run puts the same sprig and the same
           * lamp at the same offset every tile length, and the eye finds that
           * rhythm immediately - it reads as a pattern of spots rather than as
           * garland. Flipping alternate tiles doubles the period and breaks it.
           */
          const flip = i % 2 === 1 ? -1 : 1
          sp.width = thick * (cap ? 0.92 : 1) * flip
          sp.height = tileLen * (cap ? 0.8 : 1.06)
          const k = 1 - 0.7 * night
          const ch = (v: number) => Math.round(Math.min(255, v * k))
          sp.tint = (ch(250) << 16) | (ch(250) << 8) | ch(255)
          refs.wireLayer.addChild(sp)

          // The lamps are already in the picture, so light them rather than
          // drawing a second set on top.
          const re = new Sprite(tex)
          re.anchor.set(0.5)
          re.position.set(px2, py2)
          re.rotation = sp.rotation
          re.width = sp.width
          re.height = sp.height
          re.blendMode = 'add'
          re.alpha = 0.1 + 0.34 * night
          refs.glowLayer.addChild(re)
        }

        if (!isDraft) {
          const pixels = pathLength(px)
          totalPixels += pixels
          totalBulbs += count
          perStrand.push({
            id: strand.id,
            name: strand.name,
            bulbs: count,
            pixels,
            feet: ppf ? measuredFeet(px, ppf, strand, doc.takeoff) : null,
          })
        }
        continue
      }

      // No artwork yet: the drawn rope, so a run is never invisible.
      paintGreenery(
        samples.map((q) => ({ x: q.x, y: q.y })),
        Math.max(3, baseSize * 3.4),
        px[0].x * 0.31 + px[0].y * 0.77,
      )
    }

    // The wire the bulbs hang from. Thin, dark, low alpha. Its absence is
    // noticeable long before anyone can say why.
    const wirePath = spine ?? samples
    if (wirePath.length > 1) {
      refs.wireLayer.moveTo(wirePath[0].x, wirePath[0].y)
      for (let i = 1; i < wirePath.length; i++) refs.wireLayer.lineTo(wirePath[i].x, wirePath[i].y)
      /*
       * YOU CANNOT SEE THE WIRE AT NIGHT.
       *
       * On the reference photograph there is no wire anywhere: between two
       * bulbs there is lit fascia and nothing else. Ours drew a green line at
       * a flat 0.55 whatever the hour, and against a properly dark night it
       * was one of the more visible things in the frame - a drawn line
       * connecting the dots, which is exactly the tell we are trying to lose.
       *
       * In daylight it still matters, because a run of unlit bulbs with no
       * wire between them floats.
       */
      refs.wireLayer.stroke({
        width: Math.max(0.8, baseSize * 0.16),
        color: hexToNumber(spec.wire),
        alpha: (spine ? 0.22 : 0.55) * (1 - 0.88 * night),
        cap: 'round',
        join: 'round',
      })
    }

    /*
     * How tightly this run is packed, relative to C9 spacing. Mini lights sit
     * three inches apart; at the old halo sizes their spills overlapped four
     * deep and the run turned into a lit tube. This pulls the wide, additive
     * parts down as the bulbs crowd, and leaves the tight core alone, which is
     * what keeps individual lamps countable at any product.
     */
    /*
     * The crowd damping was eating the glow on ordinary C9 runs.
     *
     * It exists so a tight mini strand does not blow out where a spaced C9 run
     * looks right, and that is worth keeping. But a C9 at twelve inches was
     * landing at 0.5 and taking half the halo with it, which is why a roofline
     * exported as hard little dots with nothing around them. Its floor is
     * higher and it no longer bites until the bulbs are genuinely crowded.
     */
    const crowd = Math.max(0.62, Math.min(1, baseSpacing / Math.max(1, baseSize * 6)))

    /*
     * THE LIGHT THE RUN THROWS ON THE HOUSE.
     *
     * This is the single thing that separated our render from a photograph of
     * a real install, and it is not a property of a bulb at all.
     *
     * Measured off a night shot of one of our own jobs: the fascia and the
     * whole gable face behind a run are washed warm, at a luminance around
     * 100, while the sky two feet above them is at 1.6. Fifty bulbs a few
     * inches apart do not each throw a little pool - their pools merge into a
     * band of light down the whole length of the run, and that band is what
     * your eye reads as "those lights are ON that house". Without it the bulbs
     * hang in front of the picture like stickers, which is what ours did.
     *
     * It goes down before anything else so the bulbs sit inside their own
     * glow, and it is offset toward the surface, because a bulb on a fascia
     * faces out and DOWN: the wash lands under the run, not around it.
     *
     * One band per run rather than one blob per bulb, because that is both
     * what it looks like and a fiftieth of the sprites.
     */
    /*
     * A FROND HAS NOTHING UNDER IT TO WASH.
     *
     * The band below is offset toward the surface a run sits on, because a
     * bulb on a fascia faces out and down. An arching palm frond is in the
     * air: the wash would hang under the leaf with nothing to land on, which
     * is the same reason a bush does not get one.
     */
    if (
      night > 0.04 &&
      bounce > 0.01 &&
      samples.length > 1 &&
      strand.kind !== 'freeform' &&
      strand.kind !== 'frond'
    ) {
      const bandW = baseSize * 26
      const step = Math.max(1, bandW * 0.28)
      /*
       * WHAT COLOR THE HOUSE GOES.
       *
       * Not the bulb's color. On the reference photograph the tile under a
       * warm white run reads (116, 102, 56): the red and green are close, the
       * blue is barely half of them. Stucco and clay do not reflect blue back
       * at you under a warm lamp, and a wash tinted with the bulb's own color
       * came out gray - which is why the lit roof in our render was (108, 103,
       * 103) against a photograph's (116, 102, 56).
       *
       * So the wash takes the bulb's color and pulls the blue down hard.
       */
      const tintW = surfaceUnder(colors[0] ?? 0xffb55a)
      let carried = 0
      for (let i = 1; i < samples.length; i++) {
        const a = samples[i - 1]
        const b = samples[i]
        const segLen = segmentLength(a, b)
        if (segLen < 0.0001) continue
        for (let d = carried; d < segLen; d += step) {
          const u = d / segLen
          const wx = a.x + (b.x - a.x) * u
          const wy = a.y + (b.y - a.y) * u
          const wash = new Sprite(spillTex)
          wash.anchor.set(0.5, 0.42)
          wash.position.set(wx, wy + bandW * 0.16)
          wash.width = bandW
          wash.height = bandW * 0.78
          wash.tint = tintW
          /*
           * Deliberately faint per stamp. They overlap heavily along the run
           * and additive blending sums them, so the band arrives at the right
           * strength from the run as a whole rather than from any one of them.
           */
          wash.alpha = 0.4 * bounce * night * strand.brightness
          refs.spillLayer.addChild(wash)
        }
        carried = step - ((segLen - carried) % step)
      }
    }

    /*
     * FOLIAGE POP.
     *
     * A bulb on a fascia sits against flat pale stucco: it separates from its
     * background for free, and the halo tuning that makes a C9 roofline read
     * as countable lamps was done there. A bulb in a tree or a shrub has none
     * of that. The background behind it is dark, textured and the same size as
     * the lamp, so at those settings the run vanishes into the leaves - which
     * is exactly what happened on trees and bushes.
     *
     * Foliage runs get proportionally more halo. Not a bigger bulb, which
     * would look like a C9 hanging in a tree, but more of the soft light
     * around it, because that is the part that lifts a lamp off a busy dark
     * background.
     *
     * Trunk wraps take their own path further up and already paint a much
     * wider halo per lamp, so only the freeform bush runs are handled here.
     */
    /*
     * Fronds get it too, and for exactly the reason written above: the thing
     * behind the lamps is dark, busy leaves rather than a flat pale wall, so
     * each lamp needs a wider halo to resolve against it. That is the only
     * thing a frond borrows from a bush. It is not scattered into an area and
     * it gets no mound of glow, because a frond is a line.
     */
    const foliage = strand.kind === 'freeform' || strand.kind === 'frond'
    const pop = foliage ? 1.5 : 1

    samples.forEach((s, i) => {
      const tint = colors[i % colors.length]
      const v = jitter(i + 1)
      // Real strands are never perfectly even. A few percent of variation
      // removes the stamped look without anyone noticing why.
      const vary = 0.9 + v * 0.18
      const bright = strand.brightness * spec.intensity * vary
      const size = baseSize * s.scale

      /*
       * A dark socket behind the bulb, faded out as the photo goes to night.
       * Glow is additive, and additive light on white stucco in daylight is
       * invisible by definition, so without this a run drawn on a day photo
       * looks like nothing happened at all.
       */
      const dayness = 1 - night
      if (dayness > 0.02) {
        refs.wireLayer
          .circle(s.x, s.y, size * 0.95)
          .fill({ color: 0x2a1f12, alpha: 0.55 * dayness })
      }

      /*
       * THE SOCKET, SO THE BULB IS ATTACHED TO SOMETHING.
       *
       * Ours floated. Side by side with the real thing at the same scale that
       * is the first thing you see: a row of soft blobs laid ON TOP of the
       * picture, where a photograph shows lamps sitting in the fascia with the
       * roof line running through them.
       *
       * A C9 is not a floating sphere. It screws into a socket on a clip, and
       * at night that hardware is a small dark shape underneath the glass -
       * the one part of the assembly with no light coming out of it. Drawn
       * below the bulb and before the glow, so the bloom spills over its top
       * edge exactly as it does on the real one.
       */
      if (night > 0.04) {
        refs.wireLayer.ellipse(s.x, s.y + size * 0.62, size * 0.52, size * 0.44).fill({
          color: 0x0b0a08,
          alpha: 0.62 * night,
        })
      }

      if (spill > 0.01) {
        const sp = new Sprite(spillTex)
        sp.anchor.set(0.5)
        sp.position.set(s.x, s.y)
        // See paintBulb: additive halos this wide sum along a run into a solid
        // rope with the individual lamps lost inside it.
        sp.width = sp.height = size * 9 * spill * pop
        sp.tint = tint
        sp.alpha = 0.17 * spill * bright * crowd * pop
        refs.spillLayer.addChild(sp)
      }

      /*
       * Bounce. The wash this bulb throws DOWN onto whatever it is mounted on.
       *
       * A bulb on a fascia is not floating in mid air: it faces out and down,
       * and the stucco beneath it picks up a soft warm wash that dies off over
       * a couple of feet. Anchored at the top so it hangs below the bulb, and
       * wider than it is tall because light runs sideways along a wall faster
       * than it travels down one. Only at night, because a wash this soft is
       * invisible against a sunlit wall anyway.
       */
      if (bounce > 0.01 && night > 0.04) {
        const bo = new Sprite(bounceTex)
        bo.anchor.set(0.5, 0)
        bo.position.set(s.x, s.y + size * 0.35)
        bo.width = size * 18 * bounce
        bo.height = size * 13 * bounce
        bo.tint = tint
        bo.alpha = 0.22 * bounce * bright * night * crowd
        refs.spillLayer.addChild(bo)
      }

      /*
       * Veiling glare, every fourth bulb at four times the weight. Real glass
       * scatters a little of every bright point across the whole frame, which
       * is why a night photograph of a lit house has a faint wash around the
       * display instead of sharp dots on black. The effect is cumulative, so
       * there is no reason to pay for it on every bulb.
       */
      if (glow > 0.01 && night > 0.04 && i % 4 === 0) {
        const vl = new Sprite(veilTex)
        vl.anchor.set(0.5)
        vl.position.set(s.x, s.y)
        vl.width = vl.height = size * BULB.veilSize * glow
        vl.tint = tint
        vl.alpha = BULB.veilAlpha * bright * night * crowd
        refs.glowLayer.addChild(vl)
      }

      // Two glow layers. A real bulb has a tight bright halo close in and a
      // much wider, much fainter one. A single blurred circle reads as a
      // sticker, which is the tell on most mockup tools.
      /*
       * HOW FAR THE GLOW REACHES, measured off the reference photograph.
       *
       * Sampling rings outward from eleven bulbs on a straight eave: the core
       * holds 248 out to 4px, falls through 167 at 10px and 104 at 15px, and
       * is indistinguishable from the lit fascia by about 22px. The bulbs are
       * 50px apart. So the glow reaches a little under half the distance to
       * the next bulb, and ours reached well past it - which is what turned a
       * run into a continuous rope of light instead of countable lamps.
       */
      const gOuter = new Sprite(glowTex)
      gOuter.anchor.set(0.5)
      gOuter.position.set(s.x, s.y)
      /*
       * Bigger and stronger than the measurement alone asks for.
       *
       * The radial profile off the reference photograph says the glow is gone
       * by 22px with the bulbs 50px apart, and matching that exactly gave a
       * run of hard dots. The reason is scale: that photograph is one gable
       * filling a 3024px frame, and a mockup is a whole house in 1536. The
       * same physical halo is half the pixels, and half of very little is
       * nothing.
       *
       * A mockup has to read at the size it is actually looked at. This is the
       * one place that is worth more than strict accuracy, and it is what
       * separates a picture that sells the job from a technically correct one
       * nobody can see.
       */
      gOuter.width = gOuter.height = size * BULB.outerSize * glow * pop
      gOuter.tint = tint
      gOuter.alpha = BULB.outerAlpha * bright * crowd * pop
      refs.glowLayer.addChild(gOuter)

      const gInner = new Sprite(glowTex)
      gInner.anchor.set(0.5)
      gInner.position.set(s.x, s.y)
      gInner.width = gInner.height = size * BULB.innerSize * glow * (foliage ? 1.2 : 1)
      gInner.tint = tint
      gInner.alpha = BULB.innerAlpha * bright
      refs.glowLayer.addChild(gInner)

      /*
       * THE CORE IS WHITE. This one surprised me and it is worth the ink.
       *
       * Every bulb here was tinted its own color all the way through, which
       * is what a bulb looks like switched OFF. Sampled on the reference, the
       * middle of a lit warm white C9 reads (244, 251, 236) - white, with the
       * faintest green. A phone exposes for the scene, the filament is orders
       * of magnitude brighter than the scene, and it clips every channel.
       *
       * The color of a bulb lives in its GLOW and in what it throws on the
       * house, never in the middle of it. That is as true of a red bulb as a
       * warm white one: the center goes white and the red is everything
       * around it.
       */
      const c = new Sprite(coreTex)
      c.anchor.set(0.5)
      c.position.set(s.x, s.y)
      c.width = c.height = size * BULB.coreSize
      c.rotation = s.angle
      c.tint = mixTint(tint, 0xffffff, BULB.coreWhiteness)
      c.alpha = Math.min(1, BULB.coreAlpha * bright)
      refs.coreLayer.addChild(c)

      // The filament clips to white on any real exposure. Without this the
      // bulb stays the color of its glass and never reads as switched on.
      const hot = new Sprite(coreTex)
      hot.anchor.set(0.5)
      hot.position.set(s.x, s.y)
      hot.width = hot.height = size * BULB.hotSize
      hot.rotation = s.angle
      hot.tint = 0xfbfff6
      /*
       * A lit bulb is the brightest thing in a night photograph and it CLIPS.
       * Measured on the reference: the cores sit at 250 out of 255 against a
       * sky at 2. Anything less and the bulbs read as painted on rather than
       * switched on.
       */
      hot.alpha = Math.min(1, BULB.hotAlpha * bright)
      refs.coreLayer.addChild(hot)

      // The horizontal streak a bright point picks up through a real lens.
      // Only the bulbs big enough on screen to earn it, and only at night.
      if (night > 0.25 && size > 1.8) {
        const st = new Sprite(streakTex)
        st.anchor.set(0.5)
        st.position.set(s.x, s.y)
        st.width = size * 13
        st.height = size * 1.6
        st.tint = tint
        st.alpha = 0.3 * bright * night
        refs.glowLayer.addChild(st)
      }

      /*
       * SPARKLE: the star glint, on a few bulbs.
       *
       * The first version of this modeled lens diffraction - the faint smear a
       * stopped-down aperture puts on a bright point. That is what a camera
       * does and it is not what anyone means by sparkle: shown four variants of
       * it, the reaction was "I don't see the sparkles", which was correct.
       *
       * This is the graphic mark instead. Pure WHITE regardless of the bulb's
       * color, because a glint is a blown-out highlight and blown-out is white
       * by definition - tinting it amber was half of why the last attempt read
       * as a smudge. Big, sharp, and on very few lamps, since the whole effect
       * of a sparkle comes from being the exception.
       */
      const sparkle = doc.scene.sparkle ?? 0
      if (sparkle > 0.01 && night > 0.35) {
        const style = doc.scene.sparkleStyle ?? 'star'
        const pick = jitter(i * 5.3 + 11)
        /*
         * Count is its own dial now.
         *
         * Deliberately mean at the low end: four or five glints on a whole
         * roofline reads as a photograph, one on every bulb reads as a
         * Christmas card border. Even at full it never passes about one bulb
         * in six, because a sparkle only works by being the exception.
         */
        const amount = doc.scene.sparkleAmount ?? 0.3
        if (pick < 0.012 + amount * 0.16) {
          const vary = 0.72 + jitter(i * 9.1 + 3) * 0.7
          // Sized against the PHOTO, so it is visible at fit-to-window and
          // exports the same. Sizing it against the bulb is what made the
          // previous version two pixels across and invisible.
          const span = W * 0.032 * (0.5 + sparkle * 0.9) * vary
          const a = Math.min(1, (0.55 + sparkle * 0.45) * bright * night)

          if (style === 'bloom') {
            // No rays: the brightest lamps simply blow out harder.
            const bl = new Sprite(glowTex)
            bl.anchor.set(0.5)
            bl.position.set(s.x, s.y)
            bl.width = bl.height = span * 0.8
            bl.tint = tint
            bl.alpha = a * 0.5
            refs.glowLayer.addChild(bl)
          } else {
            const g = new Sprite(glintTex)
            g.anchor.set(0.5)
            g.position.set(s.x, s.y)
            g.width = g.height = span * (style === 'twinkle' ? 0.62 : 1)
            // A little off-square, so a row of them does not look stamped.
            g.rotation = (jitter(i * 3.7 + 5) - 0.5) * (style === 'star' ? 0.8 : 0.22)
            g.tint = 0xffffff
            g.alpha = a
            refs.glowLayer.addChild(g)

            // The six-point option gets a second star turned across the first,
            // which is what a six-blade aperture gives and reads as busier.
            if (style === 'star') {
              const g2 = new Sprite(glintTex)
              g2.anchor.set(0.5)
              g2.position.set(s.x, s.y)
              g2.width = g2.height = span * 0.8
              g2.rotation = g.rotation + Math.PI / 4
              g2.tint = 0xffffff
              g2.alpha = a * 0.7
              refs.glowLayer.addChild(g2)
            }
          }
        }
      }
    })

    if (isDraft) continue
    const pixels = pathLength(px)
    totalPixels += pixels
    totalBulbs += samples.length
    /*
     * HOW MUCH WIRE A BUSH ACTUALLY EATS.
     *
     * For a run along a fascia the footage is the length of the line, and that
     * is what this used to report for everything. A bush is not a line: the
     * strand is thrown back and forth over the whole face of it, so the wire
     * that comes off the spool is far longer than the squiggle drawn on the
     * photo - which is how you end up on a roof, sorry, on a driveway, three
     * strands short.
     *
     * Sockets times pitch is the honest number. A mini strand is 23.5 ft with
     * a lamp every four inches, so this lands on the same strand count you get
     * by dividing the bulbs by seventy.
     */
    const feet =
      strand.kind === 'freeform'
        ? (samples.length * spec.spacingInches * strand.spacingScale) / 12
        : ppf
          ? measuredFeet(px, ppf, strand, doc.takeoff)
          : null
    perStrand.push({
      id: strand.id,
      name: strand.name,
      bulbs: samples.length,
      pixels,
      feet,
    })
  }

  // ---- decorations ----
  for (const layer of doc.layers) {
    if (!isDecoration(layer) || !layer.visible) continue
    /*
     * Anything with artwork draws, not only wreaths.
     *
     * This filter is why garland and the bow vanished. They were placed into
     * the document, named, selected, listed in the tally and priced - and then
     * skipped here, so nothing appeared on the photo. The procedural greenery
     * further down is wreath-only and stays that way; artwork is not.
     */
    const hasArt = !!(layer.assetDataUrl || styleById(layer.styleId))
    if (layer.kind !== 'wreath' && !hasArt) continue

    const cx = layer.x * W
    const cy = layer.y * H
    // A 24in wreath by default, sized for real when the photo is calibrated.
    const radius = (ppf ? (ppf * 2) / 2 : W * 0.045) * layer.scale
    const tint = hexToNumber(layer.palette[0] ?? '#FFB55A')

    /*
     * Artwork wins over drawing when there is artwork.
     *
     * Foliage is the one thing code cannot fake convincingly, so a decoration
     * can carry a picture. The bulbs are still drawn by us on top of it, which
     * is the part that has to change per customer, and the picture is tinted
     * down as the scene goes to night so it sits in the photograph instead of
     * floating on it.
     */
    // Fall back to the standard product rather than to the drawn wreath. A
    // decoration placed before anyone opened the product picker has no styleId,
    // and treating that as "no artwork exists" is what put a hand-drawn,
    // hole-less disc on the photo when the photograph was right there.
    const style = styleById(layer.styleId) ?? defaultStyleFor(layer.kind)
    const src = layer.assetDataUrl ?? style?.url
    const art = src ? assetTexture(src) : null

    /*
     * Product photography beats anything drawn, and these are shot LIT, which
     * changes how night has to work. The bulbs are already in the picture, so
     * drawing our own on top would double every one of them. Instead the
     * greenery is darkened and the bulbs that are already there are lit back
     * up: an additive copy of the same image brightens the bright pixels far
     * more than the dark ones, so the needles fall away into the night while
     * the lamps hold. A second, larger copy at low weight is the bloom.
     */
    const litArt = art !== null && (style?.lit ?? false)
    if (art) {
      const w = radius * 2.35
      const aspect = style?.aspect ?? (art.width / art.height || 1)
      // scaleY is relative to the width scale, so a wreath left alone stays
      // round and a stretched garland keeps its own thickness.
      const yRel = (layer.scaleY ?? layer.scale) / (layer.scale || 1)
      const h = (w / aspect) * yRel

      const base = new Sprite(art)
      base.anchor.set(0.5)
      base.position.set(cx, cy)
      base.width = w
      base.height = h
      base.rotation = layer.rotation
      const k = 1 - 0.7 * night
      const ch = (v: number) => Math.round(Math.min(255, v * k))
      base.tint = (ch(250) << 16) | (ch(250) << 8) | ch(255)
      refs.wireLayer.addChild(base)

      if (litArt) {
        const relight = new Sprite(art)
        relight.anchor.set(0.5)
        relight.position.set(cx, cy)
        relight.width = w
        relight.height = h
        relight.rotation = layer.rotation
        relight.blendMode = 'add'
        /*
         * A lamp reads as lit because of what it throws, not because it is
         * bright. Additive weights the pixels that are already bright far more
         * than the dark greenery, so the bulbs bloom and the needles do not.
         * A little of it survives in daylight, which is what stops a wreath
         * looking like a switched off prop stapled to the wall.
         */
        relight.alpha = 0.1 + 0.34 * night
        refs.glowLayer.addChild(relight)

        /*
         * No scaled copy for the bloom. Drawing the same cutout larger and
         * adding it traces the wreath's own outline as a bright ring, which is
         * the exact sticker edge these assets were brought in to avoid. Bloom
         * comes from the soft radial wash below instead, which has no
         * silhouette to give away.
         */

        /*
         * The wash it throws on the wall behind it - FROM THE RING, not from
         * the middle.
         *
         * This was one big radial gradient centerd on the decoration, and the
         * brightest part of a radial gradient is its center, which on a wreath
         * is the hole. So the hole filled in with warm haze and the wreath came
         * back as a solid green blob: measured on a real render, dead center
         * read (169,142,131) where the sky should have shown straight through.
         *
         * The lamps are around the ring, so the light comes from around the
         * ring. Several small washes at the bulb radius, which is both what
         * actually happens and what leaves the hole open.
         */
        if (spill > 0.01) {
          const a = (0.06 + 0.34 * night) * spill * glow
          if (layer.kind === 'wreath') {
            const ring = Math.min(w, h) * 0.33
            const n = 10
            for (let i = 0; i < n; i++) {
              const th = (i / n) * Math.PI * 2
              const sp = new Sprite(spillTex)
              sp.anchor.set(0.5)
              sp.position.set(cx + Math.cos(th) * ring, cy + Math.sin(th) * ring)
              /*
               * Small relative to the ring, or ten overlapping gaussians meet
               * in the middle and fill the hole anyway - which is exactly what
               * the first attempt at this did, moving the measured center only
               * from (169,142,131) to (155,132,126).
               */
              sp.width = sp.height = Math.min(w, h) * 0.5
              sp.tint = tint
              // Split between the lamps rather than stacking ten full washes.
              sp.alpha = a * 0.3
              refs.spillLayer.addChild(sp)
            }
          } else {
            const sp = new Sprite(spillTex)
            sp.anchor.set(0.5)
            sp.position.set(cx, cy)
            sp.width = sp.height = w * 2.1
            sp.tint = tint
            sp.alpha = a
            refs.spillLayer.addChild(sp)
          }
        }
      }
    }

    /*
     * Artwork that already has its lamps in it does not get a full set of ours
     * as well, or every bulb doubles. It can get EXTRA ones though, on the same
     * ring and in the same colors, because the products are not all shot at
     * the same density and a sparse wreath reads as a cheap wreath.
     */
    if (litArt) {
      const extra = layer.extraBulbs ?? 0
      if (extra > 0.01 && layer.kind === 'wreath') {
        const { w: aw, h: ah } = decorationSize(doc, layer)
        const ringR = Math.min(aw, ah) * 0.325
        const bandR = Math.min(aw, ah) * 0.16
        const palette = expandPalette(style?.bulbPalette ?? layer.palette, 1)
        const size = Math.max(1.3, Math.min(aw, ah) * 0.017)
        const count = Math.round(extra * 90)
        for (let i = 0; i < count; i++) {
          // Even around the ring with jitter: purely random angles clump and
          // leave one side of the wreath dark.
          const a = ((i + jitter(i * 3.1 + layer.x * 97) * 0.85) / count) * Math.PI * 2
          const r = ringR + (jitter(i * 7.7 + 13) - 0.5) * bandR
          const bx = cx + Math.cos(a) * r
          const by = cy + Math.sin(a) * r
          // Some sit behind a sprig, which is what stops them reading as a
          // ring of dots laid over the photo.
          const buried = jitter(i * 2.3 + 51) < 0.3 ? 0.45 : 1
          paintBulb(bx, by, size, palette[i % palette.length], 0.95 * buried, 0)
        }
        totalBulbs += count
      }
      totalBulbs += 1
      continue
    }

    const ringR = radius
    const band = radius * 0.44
    const innerR = ringR - band / 2
    const outerR = ringR + band / 2
    // Dark to light. The mass takes the dark end, the surface texture the light.
    const GREENS = [0x0a1f0e, 0x102a14, 0x16371a, 0x1d4520, 0x255328, 0x2f6432]

    /*
     * Foliage is built in three layers, in the order it exists in reality.
     *
     * MASS first: a solid dark band, because a wreath is a dense object and
     * anything drawn as separate strokes alone reads as a starburst with gaps
     * you can see the sky through.
     *
     * Then the EDGE: short sprigs pointing out at the outside and in at the
     * inside, which is what breaks the two circles so it stops looking drawn.
     *
     * Then TEXTURE: short light needles across the face, which is what gives
     * it depth once the silhouette is right.
     */
    if (layer.kind !== 'wreath') continue

    if (!art) {
      refs.wireLayer
        .circle(cx, cy, ringR)
        .stroke({ width: band, color: GREENS[1], alpha: 1 })
    }

    const needle = (
      seed: number,
      rBase: number,
      outward: number,
      len: number,
      spread: number,
      idxLo: number,
      widthMul: number,
      alpha: number,
      /** Position around the ring, 0..1. Even spacing with jitter, never pure
       *  random: random angles clump and leave gaps, which is what made the
       *  first attempt look lopsided with a hole punched off center. */
      u = 0,
    ) => {
      const a = (u + (jitter(seed) - 0.5) * 0.9 * (1 / 40)) * Math.PI * 2 + jitter(seed + 77) * 0.16
      const r0 = rBase + (jitter(seed + 202) - 0.5) * band * 0.4
      const bx0 = cx + Math.cos(a) * r0
      const by0 = cy + Math.sin(a) * r0
      // Radial, but never exactly. The wrongness is the realism.
      const dir = a + (outward < 0 ? Math.PI : 0) + (jitter(seed + 101) - 0.5) * spread
      const L = len * (0.45 + jitter(seed + 303) * 1.05)
      // The lower inside of a wreath sits in its own shadow.
      const shade = Math.sin(a) > 0.15 ? 0.7 : 1
      const idx = Math.min(GREENS.length - 1, idxLo + Math.floor(jitter(seed + 404) * 2.4))
      refs.wireLayer
        .moveTo(bx0, by0)
        .lineTo(bx0 + Math.cos(dir) * L, by0 + Math.sin(dir) * L)
        .stroke({
          width: Math.max(0.5, radius * 0.028 * widthMul),
          color: GREENS[idx],
          alpha: alpha * shade,
          cap: 'round',
        })
    }

    const dens = (mul: number, lo: number, hi: number) =>
      Math.round(Math.min(hi, Math.max(lo, radius * mul)))
    const seedBase = layer.x * 911 + layer.y * 337

    // Outer fringe: what you see against the sky.
    const nOut = dens(4.2, 90, 460)
    for (let i = 0; i < nOut; i++) {
      needle(seedBase + i * 7.13, outerR, 1, band * 0.5, 1.4, 0, 1.1, 0.95, i / nOut)
    }
    // Inner fringe: closes the hole down and softens it.
    const nIn = dens(3.0, 60, 320)
    for (let i = 0; i < nIn; i++) {
      needle(seedBase + 5000 + i * 5.77, innerR, -1, band * 0.42, 1.4, 0, 1, 0.95, i / nIn)
    }
    // Surface texture across the face, lighter and short.
    const nTex = dens(5.5, 110, 560)
    for (let i = 0; i < nTex; i++) {
      needle(seedBase + 9000 + i * 3.71, ringR, 1, band * 0.36, 2.6, 3, 0.75, 0.85, i / nTex)
    }

    // Bulbs, nestled INTO the greenery rather than sitting on a perfect circle.
    // A string wound through a wreath never lands evenly and never lands flat.
    const spacing = ppf ? ppf / 3 : W * 0.012
    const count = Math.max(10, Math.round((2 * Math.PI * radius) / spacing))
    const bulb = Math.max(1.1, radius * 0.062)
    for (let i = 0; i < count; i++) {
      const jr = jitter(i * 5.3 + 11)
      const ja = jitter(i * 9.7 + 23)
      const a = ((i + (ja - 0.5) * 0.55) / count) * Math.PI * 2 - Math.PI / 2
      const r = ringR + (jr - 0.5) * band * 0.9
      const bx = cx + Math.cos(a) * r
      const by = cy + Math.sin(a) * r
      const v = jitter(i + 7)
      // Some bulbs sit behind a sprig, so they read as buried in the greenery.
      const buried = jitter(i * 2.9 + 41) < 0.28 ? 0.45 : 1
      const bright = (0.9 + v * 0.2) * buried

      const sp = new Sprite(spillTex)
      sp.anchor.set(0.5)
      sp.position.set(bx, by)
      sp.width = sp.height = bulb * 20 * spill
      sp.tint = tint
      sp.alpha = 0.4 * spill * bright
      refs.spillLayer.addChild(sp)

      const g = new Sprite(glowTex)
      g.anchor.set(0.5)
      g.position.set(bx, by)
      g.width = g.height = bulb * 10 * glow
      g.tint = tint
      g.alpha = 0.55 * bright
      refs.glowLayer.addChild(g)

      const c = new Sprite(coreTex)
      c.anchor.set(0.5)
      c.position.set(bx, by)
      c.width = c.height = bulb * 2.1
      c.tint = tint
      c.alpha = Math.min(1, 1.02 * bright)
      refs.coreLayer.addChild(c)
      totalBulbs++
    }

    /*
     * The bow. Two loops, a knot and two tails, not a chevron.
     *
     * Ribbon reads as ribbon because it turns: each loop is a closed curve that
     * comes back to the knot, and each tail flares at the cut end. Drawn in two
     * reds so the loops have a lit side and a shaded side.
     */
    const bowY = cy + outerR * 0.94
    const bw = radius * 0.8
    const RED = 0xb01623
    const RED_DARK = 0x7d0d18

    const loop = (dir: 1 | -1) => {
      refs.wireLayer
        .moveTo(cx, bowY)
        .bezierCurveTo(
          cx + dir * bw * 0.35, bowY - bw * 0.62,
          cx + dir * bw * 1.15, bowY - bw * 0.42,
          cx + dir * bw * 0.98, bowY + bw * 0.06,
        )
        .bezierCurveTo(
          cx + dir * bw * 0.86, bowY + bw * 0.42,
          cx + dir * bw * 0.3, bowY + bw * 0.24,
          cx, bowY,
        )
        .fill({ color: dir === 1 ? RED : RED_DARK, alpha: 0.97 })
    }
    loop(-1)
    loop(1)

    // Tails, flaring to a cut end.
    const tail = (dir: 1 | -1) => {
      refs.wireLayer
        .moveTo(cx, bowY)
        .bezierCurveTo(
          cx + dir * bw * 0.18, bowY + bw * 0.5,
          cx + dir * bw * 0.5, bowY + bw * 0.75,
          cx + dir * bw * 0.62, bowY + bw * 1.15,
        )
        .lineTo(cx + dir * bw * 0.3, bowY + bw * 1.05)
        .bezierCurveTo(
          cx + dir * bw * 0.28, bowY + bw * 0.66,
          cx + dir * bw * 0.08, bowY + bw * 0.4,
          cx, bowY,
        )
        .fill({ color: RED_DARK, alpha: 0.95 })
    }
    tail(-1)
    tail(1)

    // Knot last, over the join.
    refs.wireLayer
      .ellipse(cx, bowY, bw * 0.22, bw * 0.17)
      .fill({ color: RED, alpha: 1 })
  }

  return {
    totalBulbs,
    // Sum the per-run feet, which are depth corrected, rather than dividing the
    // raw pixel total. Those two disagree the moment any run recedes, and the
    // per-run figures are the ones on the quote.
    totalFeet: ppf ? perStrand.reduce((sum, m) => sum + (m.feet ?? 0), 0) : null,
    perStrand,
  }
}


/**
 * On-screen size of a decoration, in photo pixels.
 *
 * Exported because hit testing has to agree with drawing exactly. Working the
 * size out in two places is how you get a wreath you cannot click on the side
 * you can see.
 */
export function decorationSize(
  doc: LightsOsDocument,
  layer: Decoration,
): { w: number; h: number } {
  const W = doc.photo?.width ?? 1000
  const ppf = doc.calibration.pixelsPerFoot
  const style = styleById(layer.styleId)
  const radius = (ppf ? ppf : W * 0.045) * layer.scale
  const w = radius * 2.35
  const aspect = style?.aspect ?? 1
  const yRel = (layer.scaleY ?? layer.scale) / (layer.scale || 1)
  return { w, h: (w / aspect) * yRel }
}

export interface OverlayState {
  photoWidth: number
  photoHeight: number
  /** Screen to world scale, so handles stay a constant size on screen. */
  viewScale: number
  draft: Point[] | null
  hover: Point | null
  selected: Strand | null
  /** The selected decoration, with the box it occupies, so it can be resized. */
  selectedDecor: { x: number; y: number; w: number; h: number } | null
  calibA: Point | null
  calibB: Point | null
  showCrop: boolean
  /** Roof runs waiting on a person, and the confirmed ones, with their circles. */
  proposals: RoofProposal[]
  selectedProposalId: string | null
}

export function drawOverlay(refs: SceneRefs, s: OverlayState) {
  const g = refs.overlay
  g.clear()
  const W = s.photoWidth
  const H = s.photoHeight
  const k = 1 / Math.max(0.05, s.viewScale)
  const hr = 5 * k
  const lw = 1.5 * k

  // 16:9 proposal crop
  if (s.showCrop) {
    const target = 16 / 9
    let cw = W
    let ch = W / target
    if (ch > H) {
      ch = H
      cw = H * target
    }
    const x = (W - cw) / 2
    const y = (H - ch) / 2
    g.rect(0, 0, W, y).rect(0, y + ch, W, H - y - ch)
    g.rect(0, y, x, ch).rect(x + cw, y, W - x - cw, ch)
    g.fill({ color: 0x000000, alpha: 0.55 })
    g.rect(x, y, cw, ch).stroke({ width: lw, color: 0xffffff, alpha: 0.35 })
  }

  /*
   * Selected run. Anchors exist only while something is selected, so a finished
   * mockup is just lights. The emphasis is amber and thin: enough that a
   * contractor sees "this one is picked and I can drag these", not so much that
   * it reads as CAD software sitting on top of a customer's house.
   */
  if (s.selected && s.selected.points.length) {
    const pts = s.selected.points.map((p) => toPx(p, W, H))
    g.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
    // A soft halo under a thin line reads as selection without shouting.
    g.stroke({ width: lw * 5, color: 0xf1a329, alpha: 0.13 })
    g.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
    g.stroke({ width: lw * 1.1, color: 0xf1a329, alpha: 0.9 })

    for (const p of pts) {
      g.circle(p.x, p.y, hr * 1.15).fill({ color: 0x0d0f11, alpha: 0.95 })
      g.circle(p.x, p.y, hr * 1.15).stroke({ width: lw * 1.2, color: 0xf1a329, alpha: 1 })
    }
  }


  /*
   * THE CIRCLES.
   *
   * This is the whole customer-facing idea of the confirmation layer, and it
   * has to read at arm's length on a phone in a driveway: a dashed line is a
   * suggestion, a solid lit run is a decision, and a circle is a place the app
   * is asking about. Amber means the run is proposed, red means there is a
   * question at that spot that has to be answered before it can be quoted, and
   * green means somebody said yes.
   *
   * Ends, corners and peaks all get one, because those are the three places
   * the arms actually went wrong: a run that carried on past a corner, a peak
   * where two runs should have met and did not, and an end that stopped short.
   */
  const AMBER = 0xf1a329
  const RED = 0xff5a4d
  const GREEN = 0x35c46f

  const dash = (a: { x: number; y: number }, b: { x: number; y: number }, len: number, gap: number) => {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const total = Math.hypot(dx, dy)
    if (total < 1e-6) return
    const step = len + gap
    for (let t = 0; t < total; t += step) {
      const t2 = Math.min(total, t + len)
      g.moveTo(a.x + (dx * t) / total, a.y + (dy * t) / total)
      g.lineTo(a.x + (dx * t2) / total, a.y + (dy * t2) / total)
    }
  }

  for (const pr of s.proposals) {
    if (pr.status === 'rejected' || pr.points.length < 2) continue
    const pts = pr.points.map((p) => toPx(p, W, H))
    const confirmed = pr.status === 'confirmed'
    const picked = pr.id === s.selectedProposalId

    /*
     * PICKING A RUN IN THE PANEL HAS TO SHOW ON THE HOUSE.
     *
     * The list in the panel says "Roof run 2"; this is what makes that mean
     * something. A soft amber halo down the whole run, under everything else,
     * for whichever run is selected - proposed or confirmed, because a
     * confirmed run is chosen from the same list.
     */
    if (picked && pts.length > 1) {
      g.moveTo(pts[0].x, pts[0].y)
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
      g.stroke({ width: lw * 9, color: AMBER, alpha: 0.22 })
    }

    /*
     * A confirmed run is already drawn as lights, so it gets no line here -
     * only its halo and its circles, and only while it is picked. Anything
     * else would put a permanent diagram over a finished mockup, which is
     * exactly what the customer must not be shown.
     */
    if (!confirmed) {
      // Halo first so the dashes read against both a bright wall and a dark sky.
      for (let i = 1; i < pts.length; i++) {
        const undecided =
          pr.spans[i - 1] &&
          pr.spans[i - 1].visibility !== 'clear' &&
          pr.spans[i - 1].decision !== 'continues'
        g.moveTo(pts[i - 1].x, pts[i - 1].y)
        g.lineTo(pts[i].x, pts[i].y)
        g.stroke({ width: lw * 5, color: 0x000000, alpha: 0.22 })
        // A hidden stretch is drawn shorter and further apart, so the gap in
        // the line is the gap in the evidence.
        if (undecided) dash(pts[i - 1], pts[i], 4 * k, 9 * k)
        else dash(pts[i - 1], pts[i], 11 * k, 6 * k)
        g.stroke({
          width: lw * (picked ? 1.7 : 1.25),
          color: undecided ? RED : AMBER,
          alpha: picked ? 1 : 0.85,
        })
      }
    }

    if (confirmed && !picked) continue

    const ends: { at: number; warn: boolean }[] = [
      {
        at: 0,
        warn:
          pr.ends.start.sideReturnWarning &&
          pr.ends.start.sideReturnDecision !== 'trim' &&
          pr.ends.start.sideReturnDecision !== 'include',
      },
      {
        at: pts.length - 1,
        warn:
          pr.ends.end.sideReturnWarning &&
          pr.ends.end.sideReturnDecision !== 'trim' &&
          pr.ends.end.sideReturnDecision !== 'include',
      },
    ]
    const warnAt = new Map(ends.map((e) => [e.at, e.warn]))

    pts.forEach((pt, i) => {
      const isEnd = i === 0 || i === pts.length - 1
      const warn = warnAt.get(i) === true
      const color = warn ? RED : confirmed ? GREEN : AMBER
      const r = hr * (isEnd ? 1.35 : 1.05)
      g.circle(pt.x, pt.y, r).fill({ color: 0x0d0f11, alpha: confirmed ? 0.75 : 0.92 })
      g.circle(pt.x, pt.y, r).stroke({ width: lw * 1.3, color, alpha: 1 })
      // A confirmed circle is filled. Hollow means still asking.
      if (confirmed && !warn) g.circle(pt.x, pt.y, r * 0.45).fill({ color: GREEN, alpha: 1 })
      // An unanswered question wears a second ring, so it is findable on a
      // photograph without reading anything.
      if (warn) g.circle(pt.x, pt.y, r * 1.75).stroke({ width: lw, color: RED, alpha: 0.65 })
    })
  }

  /*
   * SELECTED DECORATION: a box with corners you drag.
   *
   * A slider in a side panel is a poor way to say how big a wreath is when the
   * wreath is right there on the photo. Every tool a person has ever resized
   * anything in - Canva, Keynote, Photoshop - puts a box round it with corners
   * you pull, and that is the gesture the hand already knows.
   *
   * The handles are drawn at a constant SCREEN size (k undoes the zoom), so
   * they stay grabbable whether the photo is fitted or zoomed to 400%, and are
   * generous enough to hit with a fingertip.
   */
  const sd = s.selectedDecor
  if (sd) {
    const x0 = sd.x - sd.w / 2
    const y0 = sd.y - sd.h / 2
    g.rect(x0, y0, sd.w, sd.h).stroke({ width: lw * 1.2, color: 0xf1a329, alpha: 0.85 })
    for (const [hx, hy] of [
      [x0, y0],
      [x0 + sd.w, y0],
      [x0, y0 + sd.h],
      [x0 + sd.w, y0 + sd.h],
    ]) {
      g.circle(hx, hy, hr * 1.5).fill({ color: 0xffffff, alpha: 1 })
      g.circle(hx, hy, hr * 1.5).stroke({ width: lw * 1.4, color: 0xf1a329, alpha: 1 })
    }
    /*
     * Side handles too, and only on a shape that has a length of its own.
     * A garland run wants to get longer without getting fatter; a wreath is
     * round and a width-only handle on it would just be a way to make an oval.
     */
    if (sd.w / sd.h > 1.6 || sd.h / sd.w > 1.6) {
      for (const [hx, hy] of [
        [x0, y0 + sd.h / 2],
        [x0 + sd.w, y0 + sd.h / 2],
      ]) {
        g.circle(hx, hy, hr * 1.3).fill({ color: 0xffffff, alpha: 1 })
        g.circle(hx, hy, hr * 1.3).stroke({ width: lw * 1.2, color: 0xf1a329, alpha: 1 })
      }
    }
  }

  // Strand being drawn right now, with the rubber band to the cursor
  if (s.draft && s.draft.length) {
    const pts = s.draft.map((p) => toPx(p, W, H))
    g.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y)
    if (s.hover) {
      const h = toPx(s.hover, W, H)
      g.lineTo(h.x, h.y)
    }
    g.stroke({ width: lw * 1.2, color: 0xffb55a, alpha: 0.95 })
    for (const p of pts) {
      g.circle(p.x, p.y, hr * 0.8).fill({ color: 0xffb55a, alpha: 1 })
    }
  }

  // Calibration reference
  const a = s.calibA
  const b = s.calibB
  if (a) {
    const pa = toPx(a, W, H)
    g.circle(pa.x, pa.y, hr).stroke({ width: lw * 1.4, color: 0x3ecf8e, alpha: 1 })
    if (b) {
      const pb = toPx(b, W, H)
      g.moveTo(pa.x, pa.y).lineTo(pb.x, pb.y).stroke({
        width: lw * 1.6,
        color: 0x3ecf8e,
        alpha: 1,
      })
      g.circle(pb.x, pb.y, hr).stroke({ width: lw * 1.4, color: 0x3ecf8e, alpha: 1 })
    }
  }
}
